import type {
  Alias,
  AliasInput,
  AliasMatch,
  AuditQuery,
  AuthAuditEntry,
  ClaimInput,
  ClientSecretInfo,
  CreateServiceClientInput,
  CreateTenantInput,
  CreateUserInput,
  Customer,
  CustomerInput,
  IssuedProvisioningKey,
  IssuedClientSecret,
  IssuedServiceKey,
  LocalUser,
  PageQuery,
  Paged,
  ServiceClient,
  ServiceKeyInfo,
  Tenant,
  TenantClaim,
  TenantRole,
  TenantRoleInput,
  TokenRequest,
  TokenResponse,
  UpdateServiceClientInput,
  UpdateTenantInput,
  UpdateUserInput,
  UserRole,
  LoginApp,
  SamlSettings,
  SamlSettingsInput,
} from './types';
import { errorFromResponse } from './errors';

/** How the client obtains bearer tokens; `TokenSession` implements it. */
export interface TokenProvider {
  getToken(): Promise<string | null> | string | null;
  /** Called once on 401 before giving up; returns the new token or null. */
  refresh?(): Promise<string | null>;
  onAuthExpired?(reason: 'unauthorized' | 'refresh-failed'): void;
}

export interface AuthClientOptions {
  /** `/api/auth/v1` or `https://auth.nycelogic.com/api/auth/v1` (trailing slash optional).
   *  A function is resolved on every request, so the caller can repoint the client without recreating it. */
  baseUrl: string | (() => string);
  auth: TokenProvider;
  fetch?: typeof fetch;
  /** Sent as `X-Client-Version`. */
  clientVersion?: string;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  auth?: boolean;
  signal?: AbortSignal;
}

export type AuthClient = ReturnType<typeof createAuthClient>;

const enc = encodeURIComponent;

export function createAuthClient(options: AuthClientOptions) {
  const baseUrl = () =>
    (typeof options.baseUrl === 'function' ? options.baseUrl() : options.baseUrl).replace(/\/+$/, '');
  const fetchImpl: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));

  async function headers(withAuth: boolean): Promise<Record<string, string>> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (options.clientVersion) h['X-Client-Version'] = options.clientVersion;
    if (withAuth) {
      const token = await options.auth.getToken();
      if (token) h['Authorization'] = `Bearer ${token}`;
    }
    return h;
  }

  /** One refresh on 401, then the request is retried once with the new token. */
  async function withRefresh(send: (token?: string) => Promise<Response>): Promise<Response> {
    let response = await send();
    if (response.status !== 401) return response;
    const fresh = options.auth.refresh ? await options.auth.refresh().catch(() => null) : null;
    if (!fresh) {
      options.auth.onAuthExpired?.(options.auth.refresh ? 'refresh-failed' : 'unauthorized');
      return response;
    }
    response = await send(fresh);
    if (response.status === 401) options.auth.onAuthExpired?.('unauthorized');
    return response;
  }

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    opts: RequestOptions = {}
  ): Promise<T> {
    const withAuth = opts.auth !== false;
    const send = async (token?: string) => {
      const h = await headers(withAuth);
      if (token) h['Authorization'] = `Bearer ${token}`;
      return fetchImpl(`${baseUrl()}${path}`, {
        method,
        headers: h,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: opts.signal,
      });
    };
    const response = withAuth ? await withRefresh(send) : await send();
    if (!response.ok) throw await errorFromResponse(response);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  const paged = (q: PageQuery = {}): string => {
    const s = new URLSearchParams();
    if (q.page != null) s.set('page', String(q.page));
    if (q.pageSize != null) s.set('pageSize', String(q.pageSize));
    const str = s.toString();
    return str ? `?${str}` : '';
  };

  /** `/customers/{id}/aliases` or `/tenants/{id}/aliases`. */
  const aliases = (owner: 'customers' | 'tenants') => ({
    list: (id: string) => request<Alias[]>('GET', `/${owner}/${enc(id)}/aliases`),
    /** Replaces every alias; the body is a bare JSON array. 409 when a pair belongs to another customer/tenant. */
    replace: (id: string, body: AliasInput[]) => request<void>('PUT', `/${owner}/${enc(id)}/aliases`, body),
    remove: (id: string, system: string, value: string) =>
      request<void>('DELETE', `/${owner}/${enc(id)}/aliases/${enc(system)}/${enc(value)}`),
  });

  const users = (tenantId: string) => {
    const base = `/tenants/${enc(tenantId)}/users`;
    return {
      list: (q?: PageQuery) => request<Paged<LocalUser>>('GET', `${base}${paged(q)}`),
      get: (id: string) => request<LocalUser>('GET', `${base}/${enc(id)}`),
      create: (body: CreateUserInput) => request<LocalUser>('POST', base, body),
      update: (id: string, body: UpdateUserInput) => request<LocalUser>('PUT', `${base}/${enc(id)}`, body),
      setPassword: (id: string, password: string) =>
        request<void>('POST', `${base}/${enc(id)}/password`, { password }),
      /** Soft delete: the user becomes `Disabled`. */
      remove: (id: string) => request<void>('DELETE', `${base}/${enc(id)}`),
      roles: {
        list: (id: string) => request<UserRole[]>('GET', `${base}/${enc(id)}/roles`),
        add: (id: string, role: string) => request<UserRole>('POST', `${base}/${enc(id)}/roles`, { role }),
        /** Replaces every role; the body is a bare JSON array. */
        replace: (id: string, roles: string[]) =>
          request<void>(
            'PUT',
            `${base}/${enc(id)}/roles`,
            roles.map((role) => ({ role }))
          ),
        remove: (id: string, roleId: string) =>
          request<void>('DELETE', `${base}/${enc(id)}/roles/${enc(roleId)}`),
      },
    };
  };

  return {
    /** The API root as it stands now. */
    get baseUrl() {
      return baseUrl();
    },
    request,

    /** `POST /token` — credentials → JWT* + refresh token. */
    login: (body: TokenRequest) => request<TokenResponse>('POST', '/token', body, { auth: false }),
    refresh: (refreshToken: string) =>
      request<TokenResponse>('POST', '/refresh', { refresh_token: refreshToken }, { auth: false }),
    /** `POST /revoke` — logout (needs the bearer token). */
    revoke: (refreshToken: string) => request<void>('POST', '/revoke', { refresh_token: refreshToken }),

    customers: {
      list: (q?: PageQuery) => request<Paged<Customer>>('GET', `/customers${paged(q)}`),
      get: (id: string) => request<Customer>('GET', `/customers/${enc(id)}`),
      create: (body: CustomerInput) => request<Customer>('POST', '/customers', body),
      update: (id: string, body: CustomerInput) => request<Customer>('PUT', `/customers/${enc(id)}`, body),
      aliases: aliases('customers'),
    },

    tenants: {
      list: (q?: PageQuery) => request<Paged<Tenant>>('GET', `/tenants${paged(q)}`),
      get: (id: string) => request<Tenant>('GET', `/tenants/${enc(id)}`),
      create: (body: CreateTenantInput) => request<Tenant>('POST', '/tenants', body),
      /** Full replace (name, status, identity provider, url, customer). */
      update: (id: string, body: UpdateTenantInput) => request<Tenant>('PUT', `/tenants/${enc(id)}`, body),
      /** Soft delete: status becomes `Deleted`. */
      remove: (id: string) => request<void>('DELETE', `/tenants/${enc(id)}`),
      claims: {
        list: (tenantId: string) => request<TenantClaim[]>('GET', `/tenants/${enc(tenantId)}/claims`),
        add: (tenantId: string, body: ClaimInput) =>
          request<TenantClaim>('POST', `/tenants/${enc(tenantId)}/claims`, body),
        /** Replaces every claim; the body is a bare JSON array. */
        replace: (tenantId: string, claims: ClaimInput[]) =>
          request<void>('PUT', `/tenants/${enc(tenantId)}/claims`, claims),
        remove: (tenantId: string, claimId: string) =>
          request<void>('DELETE', `/tenants/${enc(tenantId)}/claims/${enc(claimId)}`),
      },
      /** Role → scopes mappings (`/tenants/{tenantId}/roles`); empty = every user gets every tenant scope. */
      roles: {
        list: (tenantId: string) => request<TenantRole[]>('GET', `/tenants/${enc(tenantId)}/roles`),
        get: (tenantId: string, id: string) =>
          request<TenantRole>('GET', `/tenants/${enc(tenantId)}/roles/${enc(id)}`),
        /** 409 when the name is taken. */
        create: (tenantId: string, body: TenantRoleInput) =>
          request<TenantRole>('POST', `/tenants/${enc(tenantId)}/roles`, body),
        update: (tenantId: string, id: string, body: TenantRoleInput) =>
          request<TenantRole>('PUT', `/tenants/${enc(tenantId)}/roles/${enc(id)}`, body),
        /** Replaces every role; the body is a bare JSON array. Ids of surviving names are kept. */
        replace: (tenantId: string, roles: TenantRoleInput[]) =>
          request<void>('PUT', `/tenants/${enc(tenantId)}/roles`, roles),
        remove: (tenantId: string, id: string) =>
          request<void>('DELETE', `/tenants/${enc(tenantId)}/roles/${enc(id)}`),
      },
      aliases: aliases('tenants'),
      serviceKeys: {
        list: (tenantId: string) =>
          request<ServiceKeyInfo[]>('GET', `/tenants/${enc(tenantId)}/service-keys`),
        /** Issues a new key (revoking the active one) and returns the raw key once. */
        issue: (tenantId: string) =>
          request<IssuedServiceKey>('POST', `/tenants/${enc(tenantId)}/service-keys`),
        /** Revokes the active key (disables `/exchange` for the tenant). */
        revoke: (tenantId: string) => request<void>('DELETE', `/tenants/${enc(tenantId)}/service-keys`),
      },
      /** Service clients (`client_credentials`), each with up to two active secrets for overlap rotation. */
      clients: {
        list: (tenantId: string) => request<ServiceClient[]>('GET', `/tenants/${enc(tenantId)}/clients`),
        get: (tenantId: string, id: string) =>
          request<ServiceClient>('GET', `/tenants/${enc(tenantId)}/clients/${enc(id)}`),
        create: (tenantId: string, body: CreateServiceClientInput) =>
          request<ServiceClient>('POST', `/tenants/${enc(tenantId)}/clients`, body),
        /** Full replace (name, description, scopes, status). */
        update: (tenantId: string, id: string, body: UpdateServiceClientInput) =>
          request<ServiceClient>('PUT', `/tenants/${enc(tenantId)}/clients/${enc(id)}`, body),
        remove: (tenantId: string, id: string) =>
          request<void>('DELETE', `/tenants/${enc(tenantId)}/clients/${enc(id)}`),
        secrets: {
          list: (tenantId: string, id: string) =>
            request<ClientSecretInfo[]>('GET', `/tenants/${enc(tenantId)}/clients/${enc(id)}/secrets`),
          /** Returns the raw secret once; 409 when two secrets are already active. */
          issue: (tenantId: string, id: string, body: { expiresInDays?: number } = {}) =>
            request<IssuedClientSecret>('POST', `/tenants/${enc(tenantId)}/clients/${enc(id)}/secrets`, body),
          revoke: (tenantId: string, id: string, secretId: string) =>
            request<void>('DELETE', `/tenants/${enc(tenantId)}/clients/${enc(id)}/secrets/${enc(secretId)}`),
        },
      },
      /** Federated sign-in through a SAML IdP (Database tenants); GET answers before anything is configured. */
      saml: {
        get: (tenantId: string) => request<SamlSettings>('GET', `/tenants/${enc(tenantId)}/saml`),
        put: (tenantId: string, body: SamlSettingsInput) =>
          request<SamlSettings>('PUT', `/tenants/${enc(tenantId)}/saml`, body),
        remove: (tenantId: string) => request<void>('DELETE', `/tenants/${enc(tenantId)}/saml`),
      },
      /** SCIM provisioning keys: separate from service keys, one active key per tenant. */
      provisioningKeys: {
        list: (tenantId: string) =>
          request<ServiceKeyInfo[]>('GET', `/tenants/${enc(tenantId)}/provisioning-keys`),
        /** Issues a new key (revoking the active one) and returns the raw key once. */
        issue: (tenantId: string) =>
          request<IssuedProvisioningKey>('POST', `/tenants/${enc(tenantId)}/provisioning-keys`),
        /** Revokes the active key (disables SCIM provisioning for the tenant). */
        revoke: (tenantId: string) => request<void>('DELETE', `/tenants/${enc(tenantId)}/provisioning-keys`),
      },
    },

    /** Which customer (and tenant) another system's id names; 404 when no alias matches. */
    /** Admin audit trail (credentials, clients, aliases), newest first; page with `before` = the oldest `at`. */
    audit: (q: AuditQuery = {}) => {
      const s = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v != null && v !== '') s.set(k, String(v));
      const str = s.toString();
      return request<AuthAuditEntry[]>('GET', `/audit${str ? `?${str}` : ''}`);
    },

    /** Browser apps allowed to use `/authorize` (federated sign-in), global. */
    loginApps: {
      list: () => request<LoginApp[]>('GET', '/login-apps'),
      put: (id: string, body: { name: string; redirectUris: string[] }) =>
        request<LoginApp>('PUT', `/login-apps/${enc(id)}`, body),
      remove: (id: string) => request<void>('DELETE', `/login-apps/${enc(id)}`),
    },

    lookupAlias: (system: string, value: string) =>
      request<AliasMatch>('GET', `/aliases/${enc(system)}/${enc(value)}`),

    /** Local users of a `Database`-provider tenant (`/tenants/{tenantId}/users`). */
    users,
  };
}
